/**
 * Organization Training Analytics Page
 *
 * Detailed view of training module performance.
 * Route: /org/:orgSlug/analytics/training
 */

import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetOrgTrainingSummaryQuery } from '../store/api/orgAnalyticsApi';
import { useGetOrganizationBySlugQuery } from '../store/api/trainingApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { formatRate } from '../utils/scoreDisplay';

type TimePeriod = '7d' | '30d' | '90d' | 'all';

export function OrgTrainingAnalyticsPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');

  // Get org ID from slug
  const { data: org, isLoading: loadingOrg } = useGetOrganizationBySlugQuery(orgSlug || '', {
    skip: !orgSlug,
  });

  const since = useMemo(() => {
    if (timePeriod === 'all') return undefined;
    const days = timePeriod === '7d' ? 7 : timePeriod === '30d' ? 30 : 90;
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString();
  }, [timePeriod]);

  const {
    data: summary,
    isLoading: loadingSummary,
    isError: analyticsFailed,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useGetOrgTrainingSummaryQuery(
    { orgId: org?.id || '', since },
    { skip: !org?.id }
  );

  if (loadingOrg || loadingSummary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-gray-900">Organization not found</h1>
      </div>
    );
  }

  // A failed load is a failure, not an organization with no activity: never
  // fall through to the dashboard, whose counters would all read 0.
  if (analyticsFailed) {
    return (
      <div className="py-16 max-w-xl mx-auto">
        <LoadErrorNotice
          error={analyticsError}
          onRetry={() => void refetchAnalytics()}
          failedMessage="This organization's training analytics could not be loaded."
          testIdPrefix="org-training-analytics"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to={`/org/${orgSlug}/analytics`} className="text-indigo-600 hover:text-indigo-700">
          Analytics
        </Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-600">Training</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Training Analytics</h1>
          <p className="mt-1 text-gray-500">
            Performance across {summary?.totals.modules || 0} training modules
          </p>
        </div>
        <select
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Assignments</h3>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {summary?.totals.assignments || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Completion Rate</h3>
          <div className="mt-2 text-3xl font-bold text-green-600">
            {formatRate(summary?.completionRate)}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Avg Overall Score</h3>
          <div className="mt-2 text-3xl font-bold text-indigo-600">
            {summary?.averageScores.overall?.toFixed(1) || '-'}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Members with Assignments</h3>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {summary?.totals.membersWithAssignments || 0}
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Assignment Status</h2>
        <div className="grid grid-cols-4 gap-4">
          <StatusCard
            status="assigned"
            label="Assigned"
            count={summary?.assignmentStatusCounts.assigned || 0}
            color="gray"
          />
          <StatusCard
            status="in_progress"
            label="In Progress"
            count={summary?.assignmentStatusCounts.in_progress || 0}
            color="yellow"
          />
          <StatusCard
            status="completed"
            label="Completed"
            count={summary?.assignmentStatusCounts.completed || 0}
            color="green"
          />
          <StatusCard
            status="failed"
            label="Failed"
            count={summary?.assignmentStatusCounts.failed || 0}
            color="red"
          />
        </div>
      </div>

      {/* Modules Table */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Training Modules</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Module
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignments
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completion
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Score
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {summary?.byModule.map((module) => (
                <tr key={module.moduleId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{module.moduleName}</span>
                  </td>
                  <td className="px-6 py-4 text-center text-gray-600">
                    {module.assignments}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        module.completionRate !== null && module.completionRate >= 0.8
                          ? 'bg-green-100 text-green-800'
                          : module.completionRate !== null && module.completionRate >= 0.5
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {formatRate(module.completionRate)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {module.avgOverallScore != null ? (
                      <span
                        className={`font-medium ${
                          module.avgOverallScore >= 70
                            ? 'text-green-600'
                            : module.avgOverallScore >= 50
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {module.avgOverallScore.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/org/${orgSlug}/analytics/training/modules/${module.moduleId}`}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
              {(!summary?.byModule || summary.byModule.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No training modules found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusCard({
  label,
  count,
  color,
}: {
  status: string;
  label: string;
  count: number;
  color: 'gray' | 'yellow' | 'green' | 'red';
}) {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-900',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className={`text-center p-4 rounded-lg ${colorClasses[color]}`}>
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

export default OrgTrainingAnalyticsPage;
